import React, { FC, useEffect } from 'react';

import Button from 'components/common/ui-kit/Button';
import Loader from 'components/common/ui-kit/Loader';
import useAppDispatch from 'hooks/common/useAppDispatch';
import useAppSelector from 'hooks/common/useAppSelector';
import terms from 'i18n';
import { FileId, PreviewFilesRequest } from 'models/files';
import { createToast } from 'redux/actions';
import { achievementsApi } from 'redux/api/applications/achievements-api';
import { olympiadsApi } from 'redux/api/applications/olympiads-api';
import { privilegesApi } from 'redux/api/applications/privileges-api';

import ModeratorFile from '../../../components/moderator/ModeratorFile';
import {
  useLazyDownloadFileQuery,
  useLazyDownloadFilesQuery,
  useLazyPreviewFilesQuery,
} from '../../../redux/api/common/files-api';
import {
  selectCurrentApplication,
  selectCurrentApplicationUser,
  selectCurrentQuestionnaire,
} from '../../../redux/selectors/staff/applications';
import {
  selectCurrentSupportQuestionnaire,
  selectCurrentSupportUser,
} from '../../../redux/selectors/staff/questionnaires';
import { downloadFile } from '../../../utils/common';

const ModeratorDocumentsPage: FC<{ isSupport?: boolean }> = ({ isSupport }) => {
  const dispatch = useAppDispatch();
  const application = useAppSelector(selectCurrentApplication);
  const questionnaire = useAppSelector(
    isSupport ? selectCurrentSupportQuestionnaire : selectCurrentQuestionnaire
  );
  const user = useAppSelector(
    isSupport ? selectCurrentSupportUser : selectCurrentApplicationUser
  );

  const [getFilesMeta, { data: fileMeta }] = useLazyPreviewFilesQuery();
  const [download] = useLazyDownloadFileQuery();

  const { data: olympiads, isLoading: isOlympiadsListLoading } =
    olympiadsApi.useGetOlympiadsByApplicationIdQuery(application?.id ?? 0, {
      skip: !application,
    });

  const { data: achievements, isLoading: isAchievementsListLoading } =
    achievementsApi.useGetAchievementsByApplicationIdQuery(
      application?.id ?? 0,
      {
        skip: !application,
      }
    );

  const [downloadAllFiles, { isLoading: isDownloading }] =
    useLazyDownloadFilesQuery();

  const { data: privileges, isLoading: isPrivilegesListLoading } =
    privilegesApi.useGetPrivilegesByApplicationIdQuery(application?.id ?? 0, {
      skip: !application,
    });

  useEffect(() => {
    if (!questionnaire || !user) return;
    const ids = [
      questionnaire.legalRepresentativeForm.personalDataConsentId,
      questionnaire.passportForm.minobrReferralUploadId,
      questionnaire.passportForm.passportCopyUploadId,
      questionnaire.passportForm.temporaryResidencePermitUploadId,
      questionnaire.compatriotForm.applicationOfRecognitionUploadId,
      questionnaire.compatriotForm.birthCertificateUploadId,
      ...questionnaire.compatriotForm.relativesBirthCertificatesUploadIds,
      questionnaire.educationalInformationForm[0].documentCopyUploadId,
    ] as FileId[];

    if (olympiads) {
      ids.push(...olympiads.map(olympiad => olympiad.documentId));
    }

    if (achievements) {
      ids.push(...achievements.map(achievement => achievement.documentId));
    }

    if (privileges) {
      ids.push(...privileges.map(privilege => privilege.documentId));
    }

    getFilesMeta({
      userId: user.id,
      ids: ids.filter(id => id !== undefined),
    });
  }, [questionnaire, user, olympiads, achievements, privileges]);

  const fileIds = fileMeta?.map(f => f.id) ?? [];

  const onDownloadAllFiles = async (data: PreviewFilesRequest) => {
    const { userId, ids } = data;

    if (!userId || !ids || ids.length === 0) return;

    const validIds = ids.filter((id): id is number => id !== null);

    try {
      const result = await downloadAllFiles({
        userId,
        ids: validIds,
      }).unwrap();

      downloadFile(
        new File(
          [result],
          `document_copies_${application?.id || 'unknown'}.zip`
        )
      );
    } catch (error) {
      dispatch(createToast(terms.DOWNLOAD_FILE_ERROR, 'danger'));
    }
  };

  const handleDownloadFiles = () => {
    if (!user) return;
    const userId = user.id;
    if (!userId || !fileIds || fileIds.length === 0) return;

    const request: PreviewFilesRequest = {
      userId,
      ids: fileIds,
    };

    onDownloadAllFiles(request);
  };

  if (
    !questionnaire ||
    !fileMeta ||
    isOlympiadsListLoading ||
    isAchievementsListLoading ||
    isPrivilegesListLoading
  ) {
    return <Loader />;
  }

  return (
    <>
      <div className="moderator-documents-btn">
        <Button theme="success" onClick={handleDownloadFiles}>
          Скачать все
        </Button>
      </div>

      <div className="moderator-documents-page">
        {fileMeta.length !== 0 &&
          fileMeta.map((file, index) => (
            <ModeratorFile
              key={index}
              title={file.originalName}
              content={file.createdAt}
              upload={() => {
                download({
                  userId: user!.id,
                  fileId: file.id,
                })
                  ?.unwrap()
                  .then(result => {
                    downloadFile(new File([result], `${file.originalName}`));
                  });
              }}
            />
          ))}
      </div>
    </>
  );
};

export default ModeratorDocumentsPage;
